/**
 * TOTP (Time-based One-Time Password) setup component.
 * Guides users through enabling 2FA with authenticator apps.
 */

import { useState, useEffect, useCallback } from 'react';
import type { UiNodeInputAttributes } from '@ory/client';
import { kratos } from '../lib/kratos';
import QRCode, { generateTOTPUri } from './QRCode';
import LoadingSpinner from './LoadingSpinner';

interface TOTPSetupProps {
  /** Callback when TOTP is successfully enabled */
  onSuccess?: () => void;
  /** Callback when setup is cancelled */
  onCancel?: () => void;
  /** Current settings flow ID */
  flowId: string;
}

type SetupPhase = 'initial' | 'qr' | 'verify' | 'success' | 'error' | 'reauth';

export default function TOTPSetup({ onSuccess, onCancel, flowId }: TOTPSetupProps) {
  const [phase, setPhase] = useState<SetupPhase>('initial');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [totpUri, setTotpUri] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  // Fetch TOTP setup nodes from settings flow
  const initializeTOTP = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: flow } = await kratos.getSettingsFlow({ id: flowId });

      // Find TOTP nodes
      const totpNodes = flow.ui.nodes.filter(
        (node) => node.group === 'totp'
      );

      // Kratos provides TOTP setup via these node types:
      // - 'img' node with id='totp_qr' contains QR code as data URI
      // - 'text' node with id='totp_secret_key' contains the secret
      // - 'input' node with name='totp_code' for verification

      // Get CSRF token
      const csrfNode = flow.ui.nodes.find((node) => {
        const attrs = node.attributes as UiNodeInputAttributes;
        return attrs.name === 'csrf_token';
      });

      if (csrfNode) {
        const attrs = csrfNode.attributes as UiNodeInputAttributes;
        setCsrfToken(attrs.value as string);
      }

      // Get user email from identity
      const email = (flow.identity?.traits?.email as string) || 'user';

      // Debug: log what nodes we found
      console.log('TOTP nodes found:', totpNodes.map(n => ({
        group: n.group,
        type: n.type,
        attrs: n.attributes
      })));

      // Try to extract TOTP setup data from various node formats
      let foundSetup = false;
      let qrImageSrc: string | null = null;

      // First pass: collect all the data we can find
      for (const node of totpNodes) {
        const attrs = node.attributes as unknown as Record<string, unknown>;
        console.log('Processing node:', node.type, attrs);

        // Check for image node with QR code
        if (node.type === 'img' && attrs.src) {
          qrImageSrc = attrs.src as string;
          console.log('Found QR image src');
        }

        // Check for text node with secret or URL
        if (node.type === 'text' && attrs.text) {
          const textContent = (attrs.text as { text?: string })?.text || '';
          console.log('Found text node:', textContent.substring(0, 50));

          // Check if it's an otpauth URL
          if (textContent.startsWith('otpauth://')) {
            setTotpUri(textContent);
            const match = textContent.match(/secret=([A-Z2-7]+)/i);
            if (match) {
              setTotpSecret(match[1]);
              console.log('Extracted secret from URL');
            }
            foundSetup = true;
          }
          // Check if it looks like a TOTP secret (base32, typically 16-32 chars)
          else if (/^[A-Z2-7]{16,32}$/i.test(textContent.trim())) {
            setTotpSecret(textContent.trim());
            setTotpUri(generateTOTPUri(textContent.trim(), email));
            console.log('Found raw secret');
            foundSetup = true;
          }
        }

        // Check for input node (totp_code means we're in setup mode)
        if (attrs.name === 'totp_code') {
          console.log('Found totp_code input - setup mode confirmed');
        }
      }

      // If we found a QR image but no URL/secret yet, we still have setup available
      // The QR image contains the otpauth URL encoded, user can scan it
      if (qrImageSrc && !foundSetup) {
        console.log('Using QR image directly (no text secret found)');
        // We don't have the raw secret, but we have the QR - that's enough
        // Set a placeholder - the QR contains the real data
        setTotpUri(qrImageSrc); // Store the image src temporarily
        foundSetup = true;
      }

      if (foundSetup) {
        setPhase('qr');
      }

      if (!foundSetup) {
        // Check if TOTP is already enabled
        const totpUnlinkNode = totpNodes.find((node) => {
          const attrs = node.attributes as UiNodeInputAttributes;
          return attrs.name === 'totp_unlink';
        });

        if (totpUnlinkNode) {
          setError('TOTP is already enabled on your account.');
          setPhase('error');
        } else if (totpNodes.length === 0) {
          setError('TOTP is not available. Please ensure 2FA is enabled in your account settings.');
          setPhase('error');
        } else {
          // Log the nodes we found for debugging
          console.error('TOTP nodes found but could not extract setup data:', totpNodes);
          setError('Could not initialize TOTP setup. Please try again.');
          setPhase('error');
        }
      }
    } catch (err) {
      console.error('TOTP init error:', err);
      setError('Failed to initialize TOTP setup. Please refresh and try again.');
      setPhase('error');
    } finally {
      setIsLoading(false);
    }
  }, [flowId]);

  useEffect(() => {
    if (phase === 'initial') {
      initializeTOTP();
    }
  }, [phase, initializeTOTP]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await kratos.updateSettingsFlow({
        flow: flowId,
        updateSettingsFlowBody: {
          method: 'totp',
          totp_code: verificationCode,
          csrf_token: csrfToken || undefined,
        } as Parameters<typeof kratos.updateSettingsFlow>[0]['updateSettingsFlowBody'],
      });

      setPhase('success');
      onSuccess?.();
    } catch (err: unknown) {
      console.error('TOTP verify error:', err);

      // Extract error message from Kratos response
      if (err && typeof err === 'object' && 'response' in err) {
        const response = err as {
          response?: {
            status?: number;
            data?: {
              ui?: { messages?: Array<{ text: string }> };
              error?: { reason?: string; message?: string };
            }
          }
        };

        // Check for 403 Forbidden - privileged session required
        if (response.response?.status === 403) {
          const reason = response.response?.data?.error?.reason || '';
          if (reason.includes('session is too old') || reason.includes('re-authenticate')) {
            setPhase('reauth');
            return;
          }
        }

        const messages = response.response?.data?.ui?.messages;
        if (messages && messages.length > 0) {
          setError(messages[0].text);
        } else {
          setError('Invalid verification code. Please try again.');
        }
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits, limit to 6
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  // Initial/loading state
  if (phase === 'initial' || (isLoading && phase !== 'qr')) {
    return (
      <div className="text-center py-8">
        <LoadingSpinner text="Setting up 2FA..." />
      </div>
    );
  }

  // Re-authentication required state
  if (phase === 'reauth') {
    const handleReauth = () => {
      // Redirect to login with refresh=true to force re-authentication
      // and return_to to come back to settings after
      const returnTo = encodeURIComponent(window.location.pathname + '?tab=security');
      window.location.href = `/login?refresh=true&return_to=${returnTo}`;
    };

    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-content-primary mb-2">Re-authentication Required</h3>
        <p className="text-content-secondary mb-4">
          For security, you need to confirm your identity before enabling two-factor authentication.
        </p>
        <div className="flex gap-3 justify-center">
          <button onClick={onCancel} className="btn-secondary">
            Cancel
          </button>
          <button onClick={handleReauth} className="btn-primary">
            Re-authenticate
          </button>
        </div>
      </div>
    );
  }

  // Error state
  if (phase === 'error') {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <button onClick={onCancel} className="btn-secondary">
          Close
        </button>
      </div>
    );
  }

  // Success state
  if (phase === 'success') {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-content-primary mb-2">2FA Enabled!</h3>
        <p className="text-content-secondary mb-4">
          Your account is now protected with two-factor authentication.
        </p>
        <button onClick={onCancel} className="btn-primary">
          Done
        </button>
      </div>
    );
  }

  // QR code display and verification
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium text-content-primary mb-2">
          Set Up Two-Factor Authentication
        </h3>
        <p className="text-sm text-content-secondary">
          Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
        </p>
      </div>

      {/* QR Code */}
      {totpUri && (
        <div className="flex justify-center">
          {totpUri.startsWith('data:image') ? (
            // Kratos provided a pre-rendered QR image
            <div className="bg-surface-primary p-4 rounded-lg border border-border shadow-sm">
              <img src={totpUri} alt="TOTP QR Code" width={180} height={180} />
            </div>
          ) : (
            // We have an otpauth:// URL, generate QR ourselves
            <QRCode
              value={totpUri}
              size={180}
              downloadable={false}
              level="M"
            />
          )}
        </div>
      )}

      {/* Manual entry secret */}
      {totpSecret && (
        <div className="bg-surface-secondary rounded-lg p-4">
          <p className="text-xs text-content-tertiary mb-1">Can't scan? Enter this code manually:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-sm bg-surface-primary px-3 py-2 rounded border break-all">
              {totpSecret}
            </code>
            <button
              onClick={() => navigator.clipboard.writeText(totpSecret)}
              className="p-2 text-content-tertiary hover:text-content-primary"
              title="Copy secret"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Verification form */}
      <form onSubmit={handleVerify} className="space-y-4">
        <div>
          <label htmlFor="totp-code" className="block text-sm font-medium text-content-primary mb-1">
            Enter the 6-digit code from your app
          </label>
          <input
            id="totp-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={verificationCode}
            onChange={handleCodeChange}
            placeholder="000000"
            className="block w-full text-center text-2xl tracking-widest font-mono px-4 py-3 border border-border rounded-lg focus:ring-orbital-500 focus:border-orbital-500"
            maxLength={6}
            disabled={isLoading}
          />
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 btn-primary"
            disabled={isLoading || verificationCode.length !== 6}
          >
            {isLoading ? <LoadingSpinner size="sm" /> : 'Verify & Enable'}
          </button>
        </div>
      </form>
    </div>
  );
}
